import { usePresignSkillFileUpload } from "@/api/skills";
import {
  replaceBodyInMarkdown,
  stripSkillMdFrontmatter,
} from "@/utils/skillMdFrontmatter";
import { uploadPresignedSkillFile } from "@/utils/uploadSkillFile";
import { documentEditorTheme, RichTextEditor } from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

const SAVE_DEBOUNCE_MS = 800;

type SaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Rich markdown editor for any of a skill's `.md` files, with debounced autosave.
 *
 * SKILL.md is special: the editor edits the body only and re-attaches the
 * frontmatter it was seeded with on save (description edits go through the
 * PUT endpoint, which rewrites frontmatter server-side — the row is canonical,
 * so a stale frontmatter write here is cosmetic and self-heals on the next
 * edit). Every other `.md` file is edited and saved verbatim.
 */
export function SkillMarkdownEditor({
  skillId,
  filePath,
  initialContent,
}: {
  skillId: string;
  filePath: string;
  initialContent: string;
}) {
  const isSkillMd = filePath === "SKILL.md";

  // The editor is remounted (keyed) per file/skill by the parent, so `seedBody`
  // is initialized once from initialContent — the controlled value never churns
  // mid-life, which is what caused the text to flash on switch. SKILL.md edits the
  // body (frontmatter stripped); other files edit the whole document.
  const [seedBody] = useState(() =>
    isSkillMd ? stripSkillMdFrontmatter(initialContent) : initialContent,
  );
  const [status, setStatus] = useState<SaveStatus>("idle");
  const lastSavedBody = useRef(seedBody);
  const saveTimer = useRef<number | null>(null);

  const presign = usePresignSkillFileUpload();

  const uploadVerbatim = useCallback(
    async (content: string) => {
      const presigned = await presign.mutateAsync({
        skillId,
        data: { file_name: filePath },
      });
      await uploadPresignedSkillFile(
        presigned,
        new Blob([content], { type: "text/markdown" }),
        { contentType: "text/markdown" },
      );
    },
    [skillId, filePath, presign],
  );

  const queryClient = useQueryClient();

  const save = useCallback(
    async (nextBody: string) => {
      if (nextBody.trim() === lastSavedBody.current.trim()) return;
      setStatus("saving");
      try {
        if (isSkillMd) {
          // Re-attach the seeded frontmatter around the edited body; legacy
          // files without a frontmatter block are saved as-is (the server
          // heals them on the next agent edit).
          let full = nextBody;
          try {
            full = replaceBodyInMarkdown(initialContent, nextBody);
          } catch {
            // no frontmatter in the seeded content
          }
          await uploadVerbatim(full);
        } else {
          await uploadVerbatim(nextBody);
        }
        lastSavedBody.current = nextBody;
        setStatus("saved");
        // The pane's file queries carry version-pinned URLs — without this,
        // closing and reopening the pane shows the pre-save content.
        queryClient.invalidateQueries({
          queryKey: ["skill-file", skillId, filePath],
        });
        queryClient.invalidateQueries({ queryKey: ["skill-files", skillId] });
      } catch {
        setStatus("error");
        toast.error("Failed to save file");
      }
    },
    [skillId, filePath, isSkillMd, initialContent, uploadVerbatim, queryClient],
  );

  const handleChange = useCallback(
    (markdown: string) => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(
        () => save(markdown),
        SAVE_DEBOUNCE_MS,
      );
    },
    [save],
  );

  useEffect(
    () => () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    },
    [],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="mb-1.5 flex h-4 shrink-0 items-center justify-end text-xs text-muted-foreground">
        {status === "saving" && (
          <span className="inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Saving…
          </span>
        )}
        {status === "saved" && (
          <span className="inline-flex items-center gap-1">
            <Check className="h-3 w-3" />
            Saved
          </span>
        )}
        {status === "error" && (
          <span className="text-destructive">Save failed</span>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <RichTextEditor
          value={seedBody}
          onChange={handleChange}
          mode="rich"
          showToolbar={false}
          enableSkills={false}
          enableEntities={false}
          enablePlaceholders={false}
          // Render at the same scale as the TipTap document editor: doc theme
          // (16px body / em-based headings) instead of the default prose-sm.
          theme={documentEditorTheme}
          applyProseStyles={false}
          placeholder={
            isSkillMd ? "Write your skill instructions…" : "Write Markdown…"
          }
          namespace={`skill-file-${skillId}-${filePath}`}
          className="flex h-full min-h-0 flex-col border-0 bg-transparent"
          editorClassName="h-full min-h-0 max-h-none overflow-y-auto px-0 py-2 text-[16px] leading-[1.6] text-foreground"
        />
      </div>
    </div>
  );
}
